import { useCallback, useEffect, useState } from "react";
import { GiWheat } from "react-icons/gi";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { repositorioSafra } from "../../sync/repositories/agricultura";

const AJUDA = {
  titulo: "Cadastro de Safras",
  descricao:
    "Safra é o período de cultivo (ex.: 'Safra 2024/2025'), delimitado por uma data de início e término. Ela é usada nos Plantios para indicar em qual período cada plantio foi realizado — permitindo comparar produtividade entre safras diferentes.",
  secoes: [
    {
      titulo: "Dados da Safra",
      campos: [
        {
          nome: "Nome da Safra",
          obrigatorio: true,
          explicacao: "Identificação da safra, geralmente pelo período (ex.: 'Safra 2024/2025', 'Safra Verão 2024'). Aparece nas listas de seleção de Plantios.",
        },
        {
          nome: "Data de Início",
          obrigatorio: true,
          explicacao: "Data em que o período da safra começa.",
        },
        {
          nome: "Data de Término",
          obrigatorio: true,
          explicacao: "Data em que o período da safra termina. Precisa ser posterior à data de início — o sistema bloqueia o cadastro se isso não for respeitado.",
        },
        {
          nome: "Safra Ativa",
          obrigatorio: false,
          explicacao: "Marque para deixar a safra disponível para seleção em novos Plantios. Desmarcar não apaga plantios já lançados nessa safra, apenas some ela das listas de novos cadastros.",
        },
      ],
    },
  ],
  dicas: [
    "Uma safra com plantios vinculados pode não ser excluída — verifique se não há registros vinculados antes de tentar remover.",
  ],
};

export default function GestaoSafras() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [safras, setSafras] = useState([]);
  const [safrasFiltradas, setSafrasFiltradas] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  const [filtros, setFiltros] = useState({
    busca: "",
    status: "todos"
  });
  
  const [formData, setFormData] = useState({
    nome: "",
    data_inicio: "",
    data_fim: "",
    ativa: true,
  });

  // Carregar safras
  const carregarSafras = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioSafra.listar();
      const dados = response.data.results || response.data;
      setSafras(dados);
      setSafrasFiltradas(dados);
    } catch (error) {
      console.error("Erro ao carregar safras:", error);
      alert("Erro ao carregar lista de safras");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarSafras();
  }, [carregarSafras]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validarFormulario = () => {
    if (!formData.nome.trim()) {
      alert("Informe o nome da safra.");
      return false;
    }
    if (!formData.data_inicio) {
      alert("Informe a data de início.");
      return false;
    }
    if (!formData.data_fim) {
      alert("Informe a data de término.");
      return false;
    }
    
    // Validar se data_fim é posterior a data_inicio
    const dataInicio = new Date(formData.data_inicio);
    const dataFim = new Date(formData.data_fim);
    if (dataFim <= dataInicio) {
      alert("A data de término deve ser posterior à data de início.");
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const payload = {
      nome: formData.nome,
      data_inicio: formData.data_inicio,
      data_fim: formData.data_fim,
      ativa: formData.ativa,
    };

    try {
      if (editando) {
        const response = await repositorioSafra.atualizar(editandoId, payload);
        if (response.status === 200) {
          alert("Safra atualizada com sucesso!");
          handleCancel();
          carregarSafras();
        }
      } else {
        const response = await repositorioSafra.criar(payload);
        if (response.status === 201 || response.status === 200) {
          alert("Safra cadastrada com sucesso!");
          handleCancel();
          carregarSafras();
        }
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (safra) => {
    setFormData({
      nome: safra.nome || "",
      data_inicio: safra.data_inicio || "",
      data_fim: safra.data_fim || "",
      ativa: safra.ativa === true || safra.ativa === "true",
    });
    setEditando(true);
    setEditandoId(safra.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovaSafra = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      nome: "",
      data_inicio: "",
      data_fim: "",
      ativa: true,
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta safra? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioSafra.excluir(id);
      alert("Safra excluída com sucesso!");
      carregarSafras();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir safra. Verifique se não há registros vinculados.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = [...safras];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(s => 
        s.nome?.toLowerCase().includes(buscaLower)
      );
    }
    
    if (filtros.status !== "todos") {
      const ativa = filtros.status === "ativa";
      filtrados = filtrados.filter(s => s.ativa === ativa);
    }
    
    setSafrasFiltradas(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", status: "todos" });
    setSafrasFiltradas(safras);
  };

  // Formatar data
  const formatarData = (data) => {
    if (!data) return "—";
    return new Date(data).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  // Verificar safra ativa atual
  const verificarSafraAtual = (dataInicio, dataFim) => {
    const hoje = new Date();
    const inicio = new Date(dataInicio);
    const fim = new Date(dataFim);
    return hoje >= inicio && hoje <= fim;
  };

  // Obter status da safra
  const getStatusInfo = (safra) => {
    const isAtiva = safra.ativa === true || safra.ativa === "true";
    const isAtual = verificarSafraAtual(safra.data_inicio, safra.data_fim);
    
    if (isAtiva && isAtual) {
      return { label: "Em Andamento", color: "bg-green-100 text-green-800" };
    } else if (isAtiva && new Date(safra.data_fim) < new Date()) {
      return { label: "Finalizada", color: "bg-blue-100 text-blue-800" };
    } else if (isAtiva && new Date(safra.data_inicio) > new Date()) {
      return { label: "Planejada", color: "bg-yellow-100 text-yellow-800" };
    } else if (!isAtiva) {
      return { label: "Inativa", color: "bg-gray-100 text-gray-800" };
    }
    return { label: isAtiva ? "Ativa" : "Inativa", color: isAtiva ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800" };
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Safras <GiWheat size={38}/></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovaSafra}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Safra
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Safra" : "Cadastrar Nova Safra"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Linha 1: Nome da Safra */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Nome da Safra *</label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    className="w-96 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    placeholder="Ex: Safra 2024/2025, Safra Verão 2024, etc."
                    required
                  />
                </div>

                {/* Linha 2: Datas */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Data de Início *</label>
                    <input
                      type="date"
                      name="data_inicio"
                      value={formData.data_inicio}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded w-64 focus:ring-2 focus:ring-green-300"
                      required
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Data de Término *</label>
                    <input
                      type="date"
                      name="data_fim"
                      value={formData.data_fim}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded w-64 focus:ring-2 focus:ring-green-300"
                      required
                    />
                  </div>
                </div>

                {/* Linha 3: Status Ativo */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Status</label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="ativa"
                        checked={formData.ativa}
                        onChange={handleChange}
                        className="w-4 h-4 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-gray-700">Safra Ativa</span>
                    </label>
                    <span className="text-xs text-gray-500">
                      (Safras ativas podem ser usadas em novos plantios)
                    </span>
                  </div>
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Nome</label>
                  <input
                    type="text"
                    placeholder="Digite o nome da safra..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
                
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Status da Safra</label>
                  <select
                    value={filtros.status}
                    onChange={(e) => setFiltros({...filtros, status: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  >
                    <option value="todos">Todas as safras</option>
                    <option value="ativa">Safras Ativas</option>
                    <option value="inativa">Safras Inativas</option>
                  </select>
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Safras */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {safrasFiltradas.length} safra(s) encontrada(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Nome</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Data Início</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Data Término</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Duração</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Status</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ativa</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {safrasFiltradas.map((safra) => {
                        const statusInfo = getStatusInfo(safra);
                        const duracao = Math.ceil((new Date(safra.data_fim) - new Date(safra.data_inicio)) / (1000 * 60 * 60 * 24));
                        
                        return (
                          <tr key={safra.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                            <td className="p-3 text-sm font-mono">{safra.id}</td>
                            <td className="p-3 text-sm font-medium">{safra.nome}</td>
                            <td className="p-3 text-sm">{formatarData(safra.data_inicio)}</td>
                            <td className="p-3 text-sm">{formatarData(safra.data_fim)}</td>
                            <td className="p-3 text-sm">{duracao} dias</td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                                {statusInfo.label}
                              </span>
                            </td>
                            <td className="p-3 text-sm">
                              {safra.ativa ? (
                                <span className="text-green-600 flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Sim
                                </span>
                              ) : (
                                <span className="text-gray-500 flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                  Não
                                </span>
                              )}
                            </td>
                            <td className="p-3">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditar(safra)}
                                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                  title="Editar"
                                >
                                  Editar
                                </button>
                                <button
                                  onClick={() => handleExcluir(safra.id)}
                                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                  title="Excluir"
                                >
                                  Excluir
                                </button>
                              </div>
                            </td>
                           </tr>
                        );
                      })}
                      
                      {safrasFiltradas.length === 0 && (
                        <tr>
                          <td colSpan="8" className="p-8 text-center text-gray-500">
                            {filtros.busca || filtros.status !== "todos"
                              ? "Nenhuma safra encontrada com os filtros selecionados."
                              : "Nenhuma safra cadastrada."}
                           </td>
                         </tr>
                      )}
                    </tbody>
                   </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}